import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageTitle, ROLE_LABEL } from "@/components/studio/common";
import { useAuth } from "@/context/AuthContext";
import { api, dt, formatErr } from "@/lib/api";

const RoleSelect = ({ value, onChange, disabled, testid }) => (
  <Select value={value} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger data-testid={testid} className="w-48"><SelectValue /></SelectTrigger>
    <SelectContent>{Object.entries(ROLE_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

export default function Users() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", role: "collaboratore", password: "" });

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data.users));
  useEffect(() => { load(); }, []);

  const update = async (id, body) => { try { await api.patch(`/admin/users/${id}`, body); load(); toast.success("Utente aggiornato"); } catch (e) { toast.error(formatErr(e)); } };
  const create = async () => {
    try { await api.post("/admin/users", f); setOpen(false); setF({ name: "", email: "", role: "collaboratore", password: "" }); load(); toast.success("Utente creato"); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div data-testid="users-page">
      <PageTitle title="Utenti studio" sub="Ruoli: avvocato titolare (accesso completo), collaboratore, segretaria">
        {isAdmin && <Button data-testid="user-create-open-btn" onClick={() => setOpen(true)} className="bg-navy hover:bg-navy-800"><UserPlus className="h-4 w-4 mr-2" />Nuovo utente</Button>}
      </PageTitle>
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500"><tr>{["Nome", "Email", "Ruolo", "Attivo", "Creato"].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-slate-100" data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-3 font-medium text-navy">{u.name}</td><td className="px-4">{u.email}</td>
                <td className="px-4"><RoleSelect testid={`user-role-${u.email}`} value={u.role} disabled={!isAdmin || u.id === user.id} onChange={(v) => update(u.id, { role: v })} /></td>
                <td className="px-4"><Switch data-testid={`user-active-${u.email}`} checked={u.active !== false} disabled={!isAdmin || u.id === user.id} onCheckedChange={(v) => update(u.id, { active: v })} /></td>
                <td className="px-4 text-xs text-slate-500">{dt(u.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-create-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Nuovo utente</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {[["name", "Nome e cognome", "text"], ["email", "Email", "email"], ["password", "Password iniziale (min 8)", "password"]].map(([k, l, t]) => (
              <div key={k}><Label>{l}</Label><Input data-testid={`user-create-${k}`} type={t} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1" /></div>
            ))}
            <div><Label>Ruolo</Label><div className="mt-1"><RoleSelect testid="user-create-role" value={f.role} onChange={(v) => setF({ ...f, role: v })} /></div></div>
          </div>
          <DialogFooter><Button data-testid="user-create-submit" onClick={create} className="bg-navy hover:bg-navy-800">Crea utente</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
